import React, { useState, useEffect, useCallback } from "react";
import {
  listNotificationTemplates,
  listNotificationTemplatesPortal,
  createNotificationTemplate,
  createNotificationTemplatePortal,
  deleteNotificationTemplate,
  deleteNotificationTemplatePortal
} from "../../api/fees/feeApi";
import {
  Button,
  Input,
  Textarea,
  Select,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  List,
  EmptyState,
  Skeleton
} from "../../components/ui";
import {
  Plus,
  Bell
} from "lucide-react";
import {
  useToast
} from "../../context/ToastContext";

const CHANNEL_OPTIONS = [
  { label: "SMS", value: "SMS" },
  { label: "Email", value: "EMAIL" },
  { label: "WhatsApp", value: "WHATSAPP" },
];

const EVENT_OPTIONS = [
  { label: "Due reminder", value: "DUE_REMINDER" },
  { label: "Overdue reminder", value: "OVERDUE_REMINDER" },
  { label: "Receipt", value: "RECEIPT" },
  { label: "Payment success", value: "PAYMENT_SUCCESS" },
  { label: "Custom", value: "CUSTOM" },
];

const emptyForm = {
  name: "",
  channel: "SMS",
  eventType: "DUE_REMINDER",
  subject: "",
  body: "",
};

const NotificationTemplatesPage = ({ isPortal = false }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = isPortal
        ? await listNotificationTemplatesPortal()
        : await listNotificationTemplates();
      setRows(res.data || res || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to load notification templates.");
    } finally {
      setLoading(false);
    }
  }, [isPortal]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (isPortal) await createNotificationTemplatePortal(form);
      else await createNotificationTemplate(form);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to save template.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this template?")) return;
    try {
      if (isPortal) await deleteNotificationTemplatePortal(id);
      else await deleteNotificationTemplate(id);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to delete template.");
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100">Notification templates</h1>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          Configure SMS, email, and WhatsApp messages. Supported placeholders:{" "}
          <code className="rounded bg-neutral-100 dark:bg-neutral-800 px-1 text-xs">{"{{studentName}}"}</code>,{" "}
          <code className="rounded bg-neutral-100 dark:bg-neutral-800 px-1 text-xs">{"{{amount}}"}</code>,{" "}
          <code className="rounded bg-neutral-100 dark:bg-neutral-800 px-1 text-xs">{"{{outstanding}}"}</code>,{" "}
          <code className="rounded bg-neutral-100 dark:bg-neutral-800 px-1 text-xs">{"{{receiptNo}}"}</code>
        </p>
      </div>

      

      <Card>
        <CardHeader>
          <CardTitle>Create template</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <Input
              label="Template name"
              placeholder="e.g. Monthly due reminder — SMS"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <Select
                label="Channel"
                options={CHANNEL_OPTIONS}
                value={form.channel}
                onChange={(value) => setForm({ ...form, channel: value })}
              />
              <Select
                label="Event type"
                options={EVENT_OPTIONS}
                value={form.eventType}
                onChange={(value) => setForm({ ...form, eventType: value })}
              />
            </div>
            <Input
              label="Subject (email only)"
              placeholder="e.g. Fee payment reminder"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
            />
            <Textarea
              label="Message body"
              placeholder="Dear {{studentName}}, your outstanding fee of {{amount}} is due..."
              required
              rows={4}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
            />
            <Button type="submit" isLoading={submitting} leftIcon={<Plus className="h-4 w-4" />}>
              Save template
            </Button>
          </form>
        </CardContent>
      </Card>

      {loading ? (
        <div className="space-y-0 divide-y divide-neutral-100 dark:divide-neutral-800 rounded-card border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 shadow-card dark:shadow-black/20">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-start justify-between gap-3 px-4 py-3">
              <div className="min-w-0 flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <Skeleton height={16} width="35%" />
                  <Skeleton height={20} width={48} />
                  <Skeleton height={20} width={72} />
                </div>
                <Skeleton height={12} width="80%" />
                <Skeleton height={12} width="55%" />
              </div>
              <Skeleton height={28} width={56} />
            </div>
          ))}
        </div>
      ) : (
        <List
          items={rows.map((r) => ({
            id: r._id,
            content: (
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-neutral-900 dark:text-neutral-100">{r.name}</span>
                  <Badge variant="primary">{r.channel}</Badge>
                  <Badge variant="default">{(r.eventType || "").replace(/_/g, " ")}</Badge>
                </div>
                <p className="mt-1 whitespace-pre-wrap text-sm text-neutral-500 dark:text-neutral-400">{r.body}</p>
              </div>
            ),
            trailing: (
              <Button
                variant="ghost"
                size="sm"
                className="text-danger-500 hover:text-danger-700"
                onClick={() => handleDelete(r._id)}
              >
                Delete
              </Button>
            ),
          }))}
          emptyState={
            <EmptyState
              icon={<Bell />}
              title="No templates yet"
              description="Create a notification template for due reminders, receipts, or payment confirmations."
            />
          }
        />
      )}
    </div>
  );
};

export default NotificationTemplatesPage;
